import { normalizeBanWords, separateBanWords } from "./promptExclusions";
import {
  PatchLog,
  PromptLog,
  createPatchLog,
  migrateLegacyLogs,
  replayHistory,
  sameBanWords,
} from "./promptDiff";

export type PromptDraft = {
  schema: 1;
  historyFormat: 2;
  logs: PatchLog[];
  ban_words: string[];
  base_ban_words: string[];
  pendingEdit?: { before: string };
  formatRevision?: 2;
  prompt: string;
  baseVersion: number | null;
  basePrompt: string;
  updatedAt: number;
};

export const MODULE_LABELS: Record<string, string> = {
  Role: "角色定义",
  Mission: "任务目标",
  "Image understanding": "图像理解",
  "Tag selection": "标签选择",
  "Never Output Tag": "禁止输出的标签",
  Examples: "打标示例",
  "Tag format": "标签格式",
  Output: "输出格式",
  角色定义: "Role",
  建议打的标签: "Suggested tags",
  不建议打的标签: "Excluded tags",
  标签规则: "Tag rules",
  数量与质量: "Quantity and quality",
  输出格式: "Output",
};

export function moduleLabel(title: string) {
  return MODULE_LABELS[title]
    ? /[\u4e00-\u9fff]/.test(title)
      ? `${title} · ${MODULE_LABELS[title]}`
      : `${MODULE_LABELS[title]} · ${title}`
    : title;
}

type ModuleEditOption = { label: string; help: string; example: string };
const EDIT_OPTIONS: Record<string, ModuleEditOption> = {
  Role: {
    label: "AI 的判断角度",
    help: "只调整 AI 分析图片时采用的专业角度。",
    example: "例如：更关注用户会用什么词搜索这张贴纸。",
  },
  Mission: {
    label: "打标签的整体目标",
    help: "只调整标签要达到的目标和整体要求。",
    example: "例如：标签要帮助用户区分相似的贴纸模板。",
  },
  "Image understanding": {
    label: "看图时关注什么",
    help: "只调整图片中要关注或忽略的信息。",
    example: "例如：关注人物动作和场景，忽略可替换的衣服。",
  },
  "Tag selection": {
    label: "生成哪些标签",
    help: "调整标签的取舍、搜索价值和优先顺序；禁止项请选 Never Output Tag。",
    example: "例如：优先生成有明确图像依据的动作和场景标签。",
  },
  "Never Output Tag": {
    label: "禁止输出的标签",
    help: "直接输入词语表示禁止输出，已禁止则保留；解除限制请明确写“取消禁止”。",
    example: "例如：ghost（禁止）；取消禁止 ghost（允许）",
  },
  Examples: {
    label: "打标示例",
    help: "通过具体场景说明哪些标签应该保留、排除或优先排列。",
    example: "例如：挥手动作可以标 waving，不能仅凭挥手就推断 goodbye。",
  },
  "Tag format": {
    label: "标签怎么写",
    help: "调整标签用词和自然表达；字符数、小写等格式由程序处理。",
    example: "例如：优先使用常见的英文搜索短语，避免生硬直译。",
  },
  Output: {
    label: "结果怎么返回",
    help: "只调整图片描述和标签的返回要求。",
    example: "例如：图片描述更简短，不附加解释。",
  },
  建议打的标签: {
    label: "优先生成哪些标签",
    help: "只调整希望优先生成的标签类型。",
    example: "例如：优先生成动作和场景标签。",
  },
  不建议打的标签: {
    label: "不要生成哪些标签",
    help: "只调整需要排除的词或标签类型。",
    example: "例如：不要生成颜色和外貌类标签。",
  },
  数量与质量: {
    label: "标签数量和准确性",
    help: "只调整标签数量和质量要求。",
    example: "例如：宁可少生成，也不要为了凑数量添加不确定的标签。",
  },
};
const EDIT_ALIASES: Record<string, string> = {
  角色定义: "Role",
  任务目标: "Mission",
  图像理解: "Image understanding",
  标签选择: "Tag selection",
  标签规则: "Tag selection",
  标签格式: "Tag format",
  输出格式: "Output",
};
export function moduleEditOption(title: string): ModuleEditOption {
  if (!title)
    return {
      label: "按要求调整（推荐）",
      help: "不确定选哪项就用这个。直接说想怎么改，可以同时调整多个部分。",
      example: "例如：多关注动作和场景，不要生成外貌类标签。",
    };
  return (
    EDIT_OPTIONS[EDIT_ALIASES[title] || title] || {
      label: title,
      help: `只调整「${title}」这部分，其他规则保持不变。`,
      example: "说说这部分需要怎样调整…",
    }
  );
}

/** Hide infrastructure sections from the scoped editing menu, not from the prompt. */
export function selectablePromptModules(value: string) {
  const hidden = new Set(["role", "output", "角色定义", "输出格式"]);
  return extractPromptModules(value).filter(
    (title) => !hidden.has(title.toLowerCase())
  );
}

export function extractPromptModules(value: string) {
  const modules: string[] = [];
  let fence: { char: string; length: number } | undefined;
  for (const line of value.split("\n")) {
    const marker = /^ {0,3}(`{3,}|~{3,})/.exec(line);
    if (fence) {
      if (
        marker &&
        marker[1][0] === fence.char &&
        marker[1].length >= fence.length &&
        !line.slice(marker[0].length).trim()
      )
        fence = undefined;
      continue;
    }
    if (marker) {
      fence = { char: marker[1][0], length: marker[1].length };
      continue;
    }
    const heading = line
      .match(/^ {0,3}##[\t ]+(.+?)\s*$/)?.[1]
      ?.replace(/\s+#+\s*$/, "")
      .trim();
    if (heading) modules.push(heading);
  }
  // The server rejects ambiguous titles, so don't offer them as scopes.
  return modules.filter(
    (title) => modules.filter((item) => item === title).length === 1
  );
}

export function readPromptDraft(
  storage: Pick<Storage, "getItem">,
  key: string
): PromptDraft | null {
  const raw = storage.getItem(key);
  if (raw === null) return null;
  const value = JSON.parse(raw);
  if (
    value?.schema !== 1 ||
    typeof value.prompt !== "string" ||
    typeof value.basePrompt !== "string" ||
    typeof value.updatedAt !== "number" ||
    !(
      value.baseVersion === null ||
      (Number.isInteger(value.baseVersion) && value.baseVersion > 0)
    )
  ) {
    throw new Error("本地草稿格式无法读取，请先备份浏览器中的草稿数据。");
  }
  if (value.historyFormat === 2) {
    if (!Array.isArray(value.logs) || value.pendingEdit)
      throw new Error("本地草稿修改记录格式无效");
    replayHistory(value.basePrompt, value.logs, value.prompt);
    return migrateBanWords(value);
  }
  if (value.historyFormat !== undefined)
    throw new Error("不支持的草稿历史格式");
  if (value.logs !== undefined && !Array.isArray(value.logs))
    throw new Error("旧草稿日志格式无效");
  const beforePending = value.pendingEdit?.before ?? value.prompt;
  if (typeof beforePending !== "string")
    throw new Error("未完成的手动修改记录无效");
  const legacy: PromptLog[] = value.logs ?? [];
  // Earliest drafts did not have logs; retain their known starting and final texts.
  const logs =
    value.logs === undefined && value.basePrompt !== beforePending
      ? [
          createPatchLog(value.basePrompt, beforePending, 1, {
            description: "恢复已有草稿修改",
          }),
        ]
      : migrateLegacyLogs(value.basePrompt, legacy, beforePending);
  if (beforePending !== value.prompt)
    logs.push(
      createPatchLog(beforePending, value.prompt, logs.length + 1, {
        description: "恢复手动修改",
        source: "manual",
        createdAt: value.updatedAt,
      })
    );
  replayHistory(value.basePrompt, logs, value.prompt);
  const { pendingEdit, ...current } = value;
  return migrateBanWords({ ...current, historyFormat: 2, logs });
}

function migrateBanWords(value: any): PromptDraft {
  if (value.ban_words !== undefined) {
    const words = normalizeBanWords(value.ban_words);
    const base = normalizeBanWords(value.base_ban_words);
    if (
      !sameBanWords(words, value.ban_words) ||
      !sameBanWords(base, value.base_ban_words)
    )
      throw new Error("禁止标签字段格式不一致，原草稿已保留");
    replayHistory(value.basePrompt, value.logs, value.prompt, {
      base,
      final: words,
    });
    return value;
  }
  const current: PromptDraft = { ...value, ban_words: [], base_ban_words: [] };
  const migrated = reviseDraft(current, current.prompt, "迁移禁止标签列表", {
    source: "manual",
  });
  replayHistory(migrated.basePrompt, migrated.logs, migrated.prompt, {
    base: [],
    final: migrated.ban_words,
  });
  return migrated;
}

export function createPromptDraft(
  prompt: string,
  baseVersion: number | null,
  basePrompt = prompt,
  banWords: string[] = []
): PromptDraft {
  const words = normalizeBanWords(banWords);
  const initial: PromptDraft = {
    schema: 1,
    historyFormat: 2,
    formatRevision: 2,
    baseVersion,
    basePrompt,
    prompt: basePrompt,
    ban_words: words,
    base_ban_words: [...words],
    updatedAt: Date.now(),
    logs: [],
  };
  return reviseDraft(initial, prompt, "初始化草稿");
}

export function finishManualEdit(draft: PromptDraft): PromptDraft {
  if (draft.pendingEdit) throw new Error("未完成的旧草稿需要重新加载并迁移");
  return draft;
}

export function reviseDraft(
  draft: PromptDraft,
  prompt: string,
  description: string,
  context?: {
    request?: string;
    module?: string | null;
    source?: "chat" | "manual";
  },
  banWords?: string[]
): PromptDraft {
  const current = finishManualEdit(draft);
  const separated = separateBanWords(prompt, banWords ?? current.ban_words);
  prompt = separated.prompt;
  const words = separated.ban_words;
  const bansChanged = !sameBanWords(current.ban_words, words);
  if (prompt === current.prompt && !bansChanged) return current;
  const updatedAt = Date.now();
  return {
    ...current,
    prompt,
    ban_words: words,
    updatedAt,
    logs: [
      ...current.logs,
      createPatchLog(current.prompt, prompt, current.logs.length + 1, {
        description: description.slice(0, 2000),
        ...(context ? { source: "chat" as const, ...context } : {}),
        createdAt: updatedAt,
        ...(bansChanged
          ? { banWords: { before: current.ban_words, after: words } }
          : {}),
      }),
    ],
  };
}

/** Undo appends a reverse patch; the complete editing history stays inspectable. */
export function lastUndoableChange(draft: PromptDraft) {
  const active: ReturnType<typeof replayHistory> = [];
  for (const entry of replayHistory(
    draft.basePrompt,
    draft.logs,
    draft.prompt,
    { base: draft.base_ban_words, final: draft.ban_words }
  )) {
    if (entry.log.undoOf !== undefined) active.pop();
    else active.push(entry);
  }
  return active[active.length - 1];
}

export function undoDraftChange(draft: PromptDraft): PromptDraft {
  const target = lastUndoableChange(draft);
  if (!target) return draft;
  const updatedAt = Date.now();
  return {
    ...draft,
    prompt: target.before,
    ban_words: target.beforeBanWords,
    updatedAt,
    logs: [
      ...draft.logs,
      createPatchLog(draft.prompt, target.before, draft.logs.length + 1, {
        source: "manual",
        ...(!sameBanWords(draft.ban_words, target.beforeBanWords)
          ? {
              banWords: {
                before: draft.ban_words,
                after: target.beforeBanWords,
              },
            }
          : {}),
        undoOf: target.log.sequence,
        createdAt: updatedAt,
        description: `撤销：${target.log.description.split(/\r?\n/)[0]}`.slice(
          0,
          2000
        ),
        request: `撤销第 ${target.log.sequence} 次修改`,
      }),
    ],
  };
}
